package com.example.mystybag.core.security

import com.example.mystybag.config.ApplicationConfig
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.core.expected.getVersion
import com.example.mystybag.core.expected.logException
import io.ktor.util.decodeBase64Bytes
import io.ktor.util.encodeBase64
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.coroutines.suspendCoroutine
import kotlin.js.Promise

/**
 * JS implementation using the Web Crypto API directly to avoid the cryptography-kotlin
 * interface default dispatch ("Only non-blocking(suspend) calls are supported in WebCrypto").
 */
internal actual object SecurityCryptoImpl {

    private val subtle: dynamic get() = js("window.crypto.subtle")

    actual suspend fun signRequest(request: String): String? = try {
        LogHelper.logEvent("SecurityCryptoImpl.signRequest (Web Crypto)")
        val keyBytes = (Security.getSalt() + getVersion()).encodeToByteArray()
        val dataBytes = request.encodeToByteArray().encodeBase64().encodeToByteArray()
        val signature = hmacSha512Sign(keyBytes, dataBytes)
        signature.encodeBase64()
    } catch (e: Exception) {
        logException(e)
        null
    }

    actual suspend fun encryptRequest(request: String): String? = try {
        LogHelper.logEvent("SecurityCryptoImpl.encryptRequest (Web Crypto)")
        val keyBytes = Security.SOME_DATA.decodeBase64Bytes().fixUp()
        val plaintext = request.encodeToByteArray().encodeBase64().encodeToByteArray()
        aesGcmEncrypt(keyBytes, plaintext).encodeBase64()
    } catch (e: Exception) {
        logException(e)
        null
    }

    actual suspend fun signPkcs1Sha256Base64(data: ByteArray, privateKeyPem: String): String? = try {
        val keyDer = decodePemPrivateKey(privateKeyPem) ?: return null
        val key = importPkcs8PrivateKeyForRsassaPkcs1v15Sha256(keyDer)
        val signature = rsassaPkcs1v15SignSha256(key, data)
        signature.encodeBase64()
    } catch (e: Exception) {
        logException(e)
        null
    }

    actual suspend fun hashPasswordForRequest(password: String): String = try {
        val saltKey = ApplicationConfig.salt.encodeToByteArray()
        val dataBytes = password.encodeToByteArray()
        hmacSha256Sign(saltKey, dataBytes).toHexString()
    } catch (e: Exception) {
        logException(e)
        ""
    }

    private suspend fun hmacSha256Sign(keyBytes: ByteArray, dataBytes: ByteArray): ByteArray =
        suspendCoroutine { cont ->
            val keyPromise = subtle.importKey(
                "raw",
                byteArrayToArrayBuffer(keyBytes),
                js("({ name: 'HMAC', hash: 'SHA-256' })"),
                false,
                js("['sign']")
            ) as Promise<dynamic>
            keyPromise.then(
                { key: dynamic ->
                    val signPromise = subtle.sign("HMAC", key, byteArrayToArrayBuffer(dataBytes)) as Promise<dynamic>
                    signPromise.then(
                        { signature: dynamic -> cont.resume(arrayBufferToByteArray(signature)) },
                        { err: dynamic -> cont.resumeWithException(Throwable("$err")) }
                    )
                },
                { err: dynamic -> cont.resumeWithException(Throwable("$err")) }
            )
        }

    private fun ByteArray.toHexString(): String =
        joinToString("") { byte -> (byte.toInt().and(0xFF).toString(16).padStart(2, '0')) }

    private suspend fun hmacSha512Sign(keyBytes: ByteArray, dataBytes: ByteArray): ByteArray =
        suspendCoroutine { cont ->
            val keyPromise = subtle.importKey(
                "raw",
                byteArrayToArrayBuffer(keyBytes),
                js("({ name: 'HMAC', hash: 'SHA-512' })"),
                false,
                js("['sign']")
            ) as Promise<dynamic>
            keyPromise.then(
                { key: dynamic ->
                    val signPromise = subtle.sign("HMAC", key, byteArrayToArrayBuffer(dataBytes)) as Promise<dynamic>
                    signPromise.then(
                        { signature: dynamic ->
                            cont.resume(arrayBufferToByteArray(signature))
                        },
                { err: dynamic -> cont.resumeWithException(Throwable("$err")) }
                    )
                },
                { err: dynamic -> cont.resumeWithException(Throwable("$err")) }
            )
        }

    private suspend fun aesGcmEncrypt(keyBytes: ByteArray, plaintext: ByteArray): ByteArray =
        suspendCoroutine { cont ->
            val iv = randomBytes(12)
            val ivBuffer = byteArrayToArrayBuffer(iv)
            val keyPromise = subtle.importKey(
                "raw",
                byteArrayToArrayBuffer(keyBytes),
                js("({ name: 'AES-GCM' })"),
                false,
                js("['encrypt']")
            ) as Promise<dynamic>
            keyPromise.then(
                { key: dynamic ->
                    val params = js("({ name: 'AES-GCM', iv: arguments[0], tagLength: 128 })")
                    params.iv = ivBuffer
                    val encryptPromise = subtle.encrypt(
                        params,
                        key,
                        byteArrayToArrayBuffer(plaintext)
                    ) as Promise<dynamic>
                    encryptPromise.then(
                        { ciphertext: dynamic ->
                            val ct = arrayBufferToByteArray(ciphertext)
                            cont.resume(iv + ct)
                        },
                        { err: dynamic -> cont.resumeWithException(Throwable("$err")) }
                    )
                },
                { err: dynamic -> cont.resumeWithException(Throwable("$err")) }
            )
        }

    private fun byteArrayToArrayBuffer(bytes: ByteArray): dynamic {
        val len = bytes.size
        val arr = (js("(function(l) { return new Uint8Array(l); })") as (Int) -> dynamic)(len)
        for (i in 0 until len) arr[i] = bytes[i].toInt().and(0xFF)
        return arr.buffer
    }

    private fun arrayBufferToByteArray(buffer: dynamic): ByteArray {
        val arr = (js("(function(b) { return new Uint8Array(b); })") as (dynamic) -> dynamic)(buffer)
        val len = arr.length as Int
        return ByteArray(len) { (arr[it] as Int).toByte() }
    }

    private fun randomBytes(size: Int): ByteArray {
        val arr = (js("(function(l) { return new Uint8Array(l); })") as (Int) -> dynamic)(size)
        js("window.crypto.getRandomValues(arr)")
        return ByteArray(size) { (arr[it] as Int).toByte() }
    }

    private fun decodePemPrivateKey(privateKeyPem: String): ByteArray? {
        val normalized = privateKeyPem
            .replace("\\n", "\n")
            .lineSequence()
            .map { it.trim() }
            .filter { it.isNotEmpty() && !it.startsWith("-----") }
            .joinToString("")
        if (normalized.isBlank()) return null
        return try {
            normalized.decodeBase64Bytes()
        } catch (e: Exception) {
            logException(e)
            null
        }
    }

    private suspend fun importPkcs8PrivateKeyForRsassaPkcs1v15Sha256(keyDer: ByteArray): dynamic =
        suspendCoroutine { cont ->
            val importPromise = subtle.importKey(
                "pkcs8",
                byteArrayToArrayBuffer(keyDer),
                js("({ name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' })"),
                false,
                js("['sign']")
            ) as Promise<dynamic>
            importPromise.then(
                { key: dynamic -> cont.resume(key) },
                { err: dynamic -> cont.resumeWithException(Throwable("$err")) }
            )
        }

    private suspend fun rsassaPkcs1v15SignSha256(privateKey: dynamic, dataBytes: ByteArray): ByteArray =
        suspendCoroutine { cont ->
            val signPromise = subtle.sign(
                "RSASSA-PKCS1-v1_5",
                privateKey,
                byteArrayToArrayBuffer(dataBytes)
            ) as Promise<dynamic>
            signPromise.then(
                { signature: dynamic -> cont.resume(arrayBufferToByteArray(signature)) },
                { err: dynamic -> cont.resumeWithException(Throwable("$err")) }
            )
        }

    // On JS, no CryptographyProvider; use Base64 so Preferences.setSecureString/getSecureString don't throw.
    actual fun secureEncryptPreference(plainText: String): String =
        plainText.encodeToByteArray().encodeBase64()

    actual fun secureDecryptPreference(cipherText: String): String = try {
        cipherText.decodeBase64Bytes().decodeToString()
    } catch (e: Exception) {
        logException(e)
        ""
    }
}
